import React, { useEffect, useMemo, useState } from "react";
import { Eye, RefreshCw, Sparkles, Target, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  createDraftManual,
  createDraftRandomStandard,
  getBundles,
} from "../../services/QuestionBankService";

const EXAM_TYPES = ["HSK1", "HSK2", "HSK3", "HSK4"];
const SECTIONS = ["LISTENING", "READING", "WRITING"];

export default function QuestionBank({ onGoToExams }) {
  const [filters, setFilters] = useState({
    examType: "HSK1",
    section: "ALL",
    questionType: "ALL",
    q: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewBundle, setPreviewBundle] = useState(null);

  const [manualDraft, setManualDraft] = useState({
    title: "",
    type: "HSK1",
    time: 60,
  });

  const [randomStandard, setRandomStandard] = useState({
    title: "",
    type: "HSK1",
    time: 60,
  });

  const fetchBundles = async ({ page = currentPage, nextLimit = limit } = {}) => {
    try {
      setLoading(true);
      const response = await getBundles({ page, limit: nextLimit, ...filters });
      setBundles(response?.data || []);
      setCurrentPage(Number(response?.currentPage || page));
      setTotalPages(Number(response?.totalPages || 1));
      setTotalItems(Number(response?.totalItems || 0));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không tải được Question Bank");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBundles({ page: 1 });
    setCurrentPage(1);
  }, [filters.examType, filters.section, filters.questionType, limit]);

  useEffect(() => {
    fetchBundles({ page: currentPage });
  }, [currentPage]);

  const selectedCount = selectedIds.size;

  const selectedTypeMismatch = useMemo(() => {
    const selected = bundles.filter((item) => selectedIds.has(item._id));
    if (selected.length <= 1) return false;
    const type = selected[0]?.examType;
    return selected.some((item) => item.examType !== type);
  }, [bundles, selectedIds]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreateManualDraft = async () => {
    if (!selectedCount) {
      toast.error("Vui lòng chọn ít nhất 1 cụm câu hỏi");
      return;
    }
    if (selectedTypeMismatch) {
      toast.error("Các cụm đã chọn phải cùng level HSK");
      return;
    }

    try {
      const response = await createDraftManual({
        ...manualDraft,
        bundleIds: Array.from(selectedIds),
      });
      toast.success(response?.message || "Đã tạo đề nháp");
      if (typeof onGoToExams === "function") onGoToExams();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Tạo draft thủ công thất bại");
    }
  };

  const handleCreateRandomStandardDraft = async () => {
    try {
      const response = await createDraftRandomStandard({
        title: randomStandard.title,
        type: randomStandard.type,
        time: Number(randomStandard.time || 60),
      });
      toast.success(response?.message || "Đã tạo đề nháp random chuẩn");
      if (typeof onGoToExams === "function") onGoToExams();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Random chuẩn thất bại");
    }
  };

  const renderQuestionLines = (bundle) => {
    const questions = Array.isArray(bundle?.questions) ? bundle.questions : [];
    return questions.slice(0, 3).map((questionItem, index) => {
      const text =
        questionItem?.contentQuestions ||
        questionItem?.question ||
        `Câu ${index + 1}`;
      return (
        <div key={`${bundle._id}-${index}`} className="text-sm text-gray-700 leading-6 line-clamp-1">
          {index + 1}. {text}
        </div>
      );
    });
  };

  const ANSWER_LABELS = ["A", "B", "C", "D", "E", "F"];

  const renderAnswerBlock = (questionItem) => {
    if (!questionItem) return null;
    const type = String(questionItem?.type || "").toUpperCase();
    const correct = String(questionItem?.correctAnswer ?? "");

    // MT — propositions stored in mtOptions array
    if (type === "MT") {
      const opts = Array.isArray(questionItem.mtOptions) ? questionItem.mtOptions : [];
      const hasContent = opts.some((o) => o && String(o).trim());
      return (
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-500 mb-2">Propositions (mtOptions)</p>
          {hasContent ? (
            <div className="space-y-1">
              {opts.map((opt, i) => {
                const letter = ANSWER_LABELS[i] || String(i + 1);
                return (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="font-bold text-gray-500 w-5 shrink-0">{letter}.</span>
                    <span className="text-gray-800">{opt || <em className="text-gray-400">Trống</em>}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">Propositions trống (câu Listening dạng hình/âm)</p>
          )}
          {correct ? (
            <p className="mt-2 text-sm font-semibold text-green-700">
              ✓ Đáp án đúng: <span className="text-green-800">{correct}</span>
            </p>
          ) : null}
        </div>
      );
    }

    // DS — True/False
    if (type === "DS") {
      return (
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-500 mb-2">Đáp án Đúng/Sai</p>
          <div className="flex gap-3">
            {["A", "B"].map((letter) => {
              const label = letter === "A"
                ? (questionItem.contentAnswerA || "Đúng")
                : (questionItem.contentAnswerB || "Sai");
              const isCorrect = correct === letter;
              return (
                <span
                  key={letter}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                    isCorrect
                      ? "bg-green-100 border-green-400 text-green-800"
                      : "bg-gray-50 border-gray-200 text-gray-700"
                  }`}
                >
                  {letter}. {label}{isCorrect ? " ✓" : ""}
                </span>
              );
            })}
          </div>
        </div>
      );
    }

    // DL — sub questions list
    if (type === "DL") {
      const subs = Array.isArray(questionItem.subQuestions) ? questionItem.subQuestions : [];
      if (!subs.length) return <p className="mt-2 text-sm text-gray-400 italic">Không có sub-questions</p>;
      return (
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-500 mb-2">Sub-questions ({subs.length})</p>
          <div className="space-y-2">
            {subs.map((sub, si) => (
              <div key={si} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-bold text-gray-700 mb-1">{sub.question || `Câu ${si + 1}`}</p>
                {sub.contentQuestions ? <p className="text-sm text-gray-700 mb-1">{sub.contentQuestions}</p> : null}
                <div className="flex flex-wrap gap-2 mt-1">
                  {["A", "B", "C", "D"].map((letter) => {
                    const opt = sub[`contentAnswer${letter}`];
                    if (!opt) return null;
                    const isCorrect = String(sub.correctAnswer || "") === letter;
                    return (
                      <span
                        key={letter}
                        className={`px-2 py-1 rounded text-xs font-medium border ${
                          isCorrect
                            ? "bg-green-100 border-green-400 text-green-800"
                            : "bg-white border-gray-200 text-gray-700"
                        }`}
                      >
                        {letter}. {opt}{isCorrect ? " ✓" : ""}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // TN and others — contentAnswerA/B/C/D
    const options = ["A", "B", "C", "D"].map((letter) => ({
      letter,
      text: questionItem[`contentAnswer${letter}`],
    })).filter((o) => o.text);

    if (!options.length && !correct) {
      return <p className="mt-2 text-sm text-gray-400 italic">(Không có đáp án)</p>;
    }

    return (
      <div className="mt-3">
        <p className="text-xs font-semibold text-gray-500 mb-2">Lựa chọn</p>
        <div className="space-y-1">
          {options.map(({ letter, text }) => {
            const isCorrect = correct === letter;
            return (
              <div
                key={letter}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm ${
                  isCorrect
                    ? "bg-green-50 border-green-400 text-green-800 font-semibold"
                    : "bg-white border-gray-200 text-gray-700"
                }`}
              >
                <span className="font-bold shrink-0">{letter}.</span>
                <span>{text}</span>
                {isCorrect ? <span className="ml-auto text-green-600 font-bold">✓</span> : null}
              </div>
            );
          })}
        </div>
        {correct && !options.length ? (
          <p className="mt-1 text-sm font-semibold text-green-700">Đáp án: {correct}</p>
        ) : null}
      </div>
    );
  };

  const firstRowNumber = (currentPage - 1) * limit;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900">Question Bank</h2>
            <p className="text-sm text-gray-600 mt-1">Lưu theo cụm từ exam và dùng lại để tạo đề nháp mới.</p>
          </div>
          <button
            onClick={() => fetchBundles({ page: currentPage })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={16} /> Làm mới
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <select
            value={filters.examType}
            onChange={(e) => setFilters((prev) => ({ ...prev, examType: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            {EXAM_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filters.section}
            onChange={(e) => setFilters((prev) => ({ ...prev, section: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="ALL">Tất cả section</option>
            {SECTIONS.map((section) => (
              <option key={section} value={section}>{section}</option>
            ))}
          </select>

          <input
            value={filters.questionType === "ALL" ? "" : filters.questionType}
            onChange={(e) => setFilters((prev) => ({ ...prev, questionType: e.target.value.toUpperCase() || "ALL" }))}
            placeholder="Type: TN / DS / MT..."
            className="border border-gray-300 rounded-lg px-3 py-2"
          />

          <div className="flex gap-2">
            <input
              value={filters.q}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
              placeholder="Tìm nội dung"
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            />
            <button
              onClick={() => fetchBundles({ page: 1 })}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Tìm
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Target size={18} className="text-red-600" />
            <h3 className="font-bold text-gray-900">Flow 1: Tạo đề từ các câu đã chọn</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <input
              value={manualDraft.title}
              onChange={(e) => setManualDraft((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Tên đề nháp"
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
            <select
              value={manualDraft.type}
              onChange={(e) => setManualDraft((prev) => ({ ...prev, type: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              {EXAM_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              value={manualDraft.time}
              onChange={(e) => setManualDraft((prev) => ({ ...prev, time: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <button
            onClick={handleCreateManualDraft}
            className="w-full px-4 py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700"
          >
            Tạo đề nháp từ {selectedCount} cụm đã chọn
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-red-600" />
            <h3 className="font-bold text-gray-900">Flow 2: Tạo đề random theo level HSK</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Hệ thống sẽ random tự động theo cấu trúc chuẩn của level bạn chọn.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              value={randomStandard.title}
              onChange={(e) => setRandomStandard((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Tên đề nháp"
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
            <select
              value={randomStandard.type}
              onChange={(e) => setRandomStandard((prev) => ({ ...prev, type: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              {EXAM_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              value={randomStandard.time}
              onChange={(e) => setRandomStandard((prev) => ({ ...prev, time: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <button
            onClick={handleCreateRandomStandardDraft}
            className="mt-3 w-full px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
          >
            Random theo level và tạo đề nháp
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="font-bold text-gray-900">Danh sách bundle ({totalItems})</h3>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Hiển thị</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
            <span className="text-gray-600">dòng/trang</span>
          </div>
        </div>
        {loading ? <p className="text-sm text-gray-500">Đang tải...</p> : null}

        <div className="space-y-2">
          {bundles.map((bundle) => {
            const isSelected = selectedIds.has(bundle._id);
            const rowNumber = firstRowNumber + bundles.findIndex((item) => item._id === bundle._id) + 1;
            return (
              <button
                type="button"
                key={bundle._id}
                onClick={() => toggleSelect(bundle._id)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  isSelected
                    ? "border-red-400 bg-red-50 shadow"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 font-bold text-xs flex items-center justify-center border border-red-200">
                    {rowNumber}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-bold text-red-600">{bundle.examType}</span>
                      <span className="text-xs font-medium text-gray-500">{bundle.section} / {bundle.questionType || "N/A"}</span>
                    </div>

                    <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                      {bundle.examTitleText || "Không rõ đề gốc"}
                    </p>

                    <div className="mt-2 space-y-1">{renderQuestionLines(bundle)}</div>

                    <p className="mt-2 text-xs text-gray-500">
                      {bundle.bundleKind} • {bundle.leafQuestionCount} câu
                    </p>

                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPreviewBundle(bundle);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50"
                      >
                        <Eye size={14} /> Xem chi tiết
                      </button>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap border-t border-gray-200 pt-3">
          <p className="text-sm text-gray-600">
            Trang {currentPage}/{totalPages} • Tổng {totalItems} bundle
          </p>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-50"
            >
              Trang trước
            </button>
            <button
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-50"
            >
              Trang sau
            </button>
          </div>
        </div>
      </div>

      {previewBundle ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[88vh] bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold text-gray-900">Chi tiết Question Bundle</h4>
                <p className="text-sm text-gray-600 mt-1">
                  {previewBundle.examType} • {previewBundle.section} / {previewBundle.questionType || "N/A"} • {previewBundle.bundleKind}
                </p>
              </div>
              <button
                onClick={() => setPreviewBundle(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(88vh-76px)] space-y-4 bg-gray-50">
              {(previewBundle.questions || []).map((questionItem, idx) => {
                const questionLabel = questionItem?.question || `Câu ${idx + 1}`;
                return (
                  <div key={`${previewBundle._id}-preview-${idx}`} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-bold text-red-600">{questionLabel}</p>
                      <span className="text-xs text-gray-500">Type: {questionItem?.type || previewBundle.questionType || "N/A"}</span>
                    </div>

                    {questionItem?.contentQuestions ? (
                      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-6">{questionItem.contentQuestions}</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Không có question content</p>
                    )}

                    {questionItem?.imageUrl ? (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Hình ảnh</p>
                        <img
                          src={questionItem.imageUrl}
                          alt={questionLabel}
                          className="max-h-72 rounded-lg border border-gray-200"
                        />
                      </div>
                    ) : null}

                    {questionItem?.audioUrl ? (
                      <div className="mt-3 text-xs text-gray-600 break-all">
                        <span className="font-semibold">Audio: </span>
                        {questionItem.audioUrl}
                      </div>
                    ) : null}

                    {renderAnswerBlock(questionItem)}

                    {questionItem?.explain ? (
                      <div className="mt-3 text-sm text-gray-600">
                        <span className="font-semibold text-gray-500 text-xs">Giải thích: </span>
                        {questionItem.explain}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
